import { AuthError } from '@azure/msal-browser';
import {
    AxiosError,
    AxiosHeaders,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig
} from 'axios';
import { baseApi } from '.';
import { instance, loginRequest, logoutWithoutRedirect } from './auth';
import { NavigateFunction } from 'react-router-dom';

export type SetupInterceptorsParams = {
    nav: NavigateFunction;
};

const setupInterceptors = (params: SetupInterceptorsParams) => ({
    async req(config: InternalAxiosRequestConfig) {
        const abortController = new AbortController();
        config.signal = abortController.signal;

        try {
            const account = instance.getActiveAccount();

            const { accessToken } = await instance.acquireTokenSilent({
                scopes: loginRequest.scopes,
                account: account || undefined
            });

            if (config.headers) {
                (config.headers as AxiosHeaders).set('Authorization', 'Bearer ' + accessToken);
            }
        } catch (error) {
            if (error instanceof AuthError) {
                abortController.abort();
                await logoutWithoutRedirect(instance);
                params.nav('/login');
            }
        }
        return config;
    },
    reqErr(error: unknown) {
        return Promise.reject(error);
    },
    res(response: AxiosResponse) {
        return response;
    },
    async resErr(error: AxiosError) {
        if (!error.config || !error.response) return Promise.reject(error);

        const originalConfig = error.config as AxiosRequestConfigExtended<unknown>;

        originalConfig.headers = { ...originalConfig.headers } as AxiosHeaders;

        if ([401, 403].includes(error.response.status) && !originalConfig.retryCall) {
            originalConfig.retryCall = true;

            try {
                const { accessToken } = await instance.acquireTokenSilent({
                    scopes: loginRequest.scopes,
                    account: instance.getActiveAccount() || undefined
                });

                baseApi.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
            } catch (catchedError) {
                if (catchedError instanceof AuthError) {
                    await logoutWithoutRedirect(instance);
                    params.nav('/login');
                }
            }
            return baseApi(originalConfig);
        }
        return Promise.reject(error);
    }
});

export { setupInterceptors };

export type AxiosRequestConfigExtended<T> = AxiosRequestConfig<T> & {
    retryCall: boolean | undefined;
};
